"use client";

import { useEffect, useState } from "react";
import { useLocale } from "next-intl";
import { UserRound } from "lucide-react";

/** Customer portal lives on the booking subdomain. */
const PORTAL = "https://app.isg-express.com";

const ACCOUNT_LABEL: Record<string, string> = {
  de: "Mein Konto",
  en: "My account",
  fr: "Mon compte",
  nl: "Mijn account",
  it: "Il mio account",
  pl: "Moje konto",
  es: "Mi cuenta",
  cs: "Můj účet",
  zh: "我的账户",
  da: "Min konto",
};

/**
 * Header account button on the marketing site (different domain than the
 * portal). The Better-Auth session cookie lives on app.isg-express.com, so we
 * detect login state via a credentialed cross-origin call to the portal's
 * get-session endpoint (CORS-allowed via the booking app's trustedOrigins).
 *
 * Graceful degradation: defaults to "Login" → /login. Only upgrades to
 * "Mein Konto" → /auftraege when the session check confirms a user. If the
 * cross-origin call is blocked, it stays "Login", which still works (the
 * portal's /login redirects an already-logged-in user to /auftraege).
 */
export function AccountLink() {
  const locale = useLocale();
  const [loggedIn, setLoggedIn] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`${PORTAL}/api/auth/get-session`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive && d?.user) setLoggedIn(true);
      })
      .catch(() => {
        /* not logged in or cross-origin blocked → stays "Login" */
      });
    return () => {
      alive = false;
    };
  }, []);

  const href = loggedIn ? `${PORTAL}/auftraege` : `${PORTAL}/login`;
  const label = loggedIn ? ACCOUNT_LABEL[locale] ?? ACCOUNT_LABEL.de : "Login";

  return (
    <a
      href={href}
      // Login opens in a new tab (portal is a different subdomain) so the
      // marketing session/context isn't lost; "Mein Konto" navigates normally.
      target={loggedIn ? undefined : "_blank"}
      rel={loggedIn ? undefined : "noopener noreferrer"}
      className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-ink-900 transition-colors hover:text-brand-700"
    >
      <UserRound className="h-3.5 w-3.5 shrink-0 text-brand-600" aria-hidden />
      {label}
    </a>
  );
}
